import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Headset, Phone, MessageCircle, Mail, MessageSquare } from "lucide-react";

/**
 * HeaderSupportButton — small lifebuoy icon shown in the top header
 * next to the "الأخبار" shortcut. Taps open a dialog with the admin
 * support phones / WhatsApp numbers / email (same source as the owner
 * dashboard contact card).
 */
export default function HeaderSupportButton() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!open) return;
    api.get("/settings/support-info").then(r => setInfo(r.data || {})).catch(() => setInfo({}));
  }, [open]);

  const openChat = () => {
    setOpen(false);
    if (!user?.id) { nav("/login?next=/support-chat"); return; }
    nav("/support-chat");
  };

  const phones = (info?.phone_numbers || []).filter(Boolean);
  const whatsapps = (info?.whatsapp_numbers || (info?.whatsapp ? [info.whatsapp] : [])).filter(Boolean);
  const email = info?.email || "";
  const msg = info?.message || "";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="الدعم الفني"
        title="الدعم الفني"
        className="flex flex-col items-center justify-center gap-0.5 h-10 px-2 rounded-full hover:bg-primary/10 transition"
        data-testid="header-support-btn"
      >
        <Headset className="size-5 text-primary" />
        <span className="text-[9px] leading-none font-bold text-primary/90">الدعم</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-sm" data-testid="support-dialog">
          <DialogTitle className="flex items-center gap-2">
            <Headset className="size-5 text-primary" /> الدعم الفني
          </DialogTitle>
          {!info ? (
            <div className="text-center text-sm text-muted-foreground py-6">جاري التحميل…</div>
          ) : (
            <div className="space-y-3">
              {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
              <Button onClick={openChat} className="w-full gap-2 bg-primary hover:bg-primary/90 h-12 text-base font-bold" data-testid="support-open-chat">
                <MessageSquare className="size-5" /> فتح محادثة الدعم
              </Button>
              {phones.length === 0 && whatsapps.length === 0 && !email && (
                <p className="text-xs text-muted-foreground">أو تواصل مباشرة — لم تتم إضافة أرقام دعم بعد.</p>
              )}
              {phones.map((p, i) => (
                <a key={`p-${i}`} href={`tel:${p}`} className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 hover:bg-emerald-500/10 transition" data-testid={`support-call-${i}`}>
                  <span className="size-9 rounded-full bg-emerald-500/15 flex items-center justify-center"><Phone className="size-4 text-emerald-600" /></span>
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">اتصال</div>
                    <div className="font-black tabular-nums tracking-wider">{p}</div>
                  </div>
                </a>
              ))}
              {whatsapps.map((w, i) => (
                <a key={`w-${i}`} href={`https://wa.me/${w.replace(/^0/, "964").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 hover:bg-sky-500/10 transition" data-testid={`support-wa-${i}`}>
                  <span className="size-9 rounded-full bg-sky-500/15 flex items-center justify-center"><MessageCircle className="size-4 text-sky-600" /></span>
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">واتساب</div>
                    <div className="font-black tabular-nums tracking-wider">{w}</div>
                  </div>
                </a>
              ))}
              {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 hover:bg-amber-500/10 transition" data-testid="support-email">
                  <span className="size-9 rounded-full bg-amber-500/15 flex items-center justify-center"><Mail className="size-4 text-amber-600" /></span>
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">بريد</div>
                    <div className="font-black break-all text-sm">{email}</div>
                  </div>
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
